import React from "react";

/**
 * Template #3 — cylinder (3D Cutaway)
 * Explains: Pistons, Compression, Reciprocating motion, Cycles, Containers
 * Parts: piston, chamber, intake_valve, exhaust_valve
 * Actions: draw, move, highlight, fade, zoom
 *
 * 3D cylindrical chamber with depth shading, metallic piston, valve housings.
 */

export interface CylinderProps {
  visibleParts?: string[];
  highlightTarget?: string;
  fadedParts?: string[];
  opacity?: number;
  pistonPosition?: number;
  openValves?: string[];
  combustion?: boolean;
  accentColor?: string;
  labels?: { chamber?: string; intake?: string; exhaust?: string };
}

export const Cylinder: React.FC<CylinderProps> = ({
  visibleParts,
  highlightTarget,
  fadedParts = [],
  opacity = 1,
  pistonPosition = 0.5,
  openValves = [],
  combustion = false,
  accentColor = "#f59e0b",
  labels = {},
}) => {
  const allVisible = !visibleParts;
  const isVisible = (part: string) => allVisible || visibleParts!.includes(part);
  const isFaded = (part: string) => fadedParts.includes(part);
  const isHighlighted = (part: string) => highlightTarget === part;

  const cx = 400;
  const chamberTop = 80;
  const chamberH = 230;
  const chamberW = 150;
  const chamberL = cx - chamberW / 2;
  const chamberR = cx + chamberW / 2;
  const depth = 16; // 3D offset

  const pistonH = 32;
  const pistonY = chamberTop + 35 + pistonPosition * (chamberH - pistonH - 50);

  const partColor = (p: string) => {
    if (isHighlighted(p)) return accentColor;
    if (isFaded(p)) return "#374151";
    return "#64748b";
  };
  const partOp = (p: string) => (isFaded(p) ? 0.35 : 1);

  const uid = "cyl";

  return (
    <g opacity={opacity}>
      <defs>
        {/* Metallic gradient for piston */}
        <linearGradient id={`${uid}-piston`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#94a3b8" />
          <stop offset="40%" stopColor="#64748b" />
          <stop offset="100%" stopColor="#334155" />
        </linearGradient>
        {/* Chamber wall gradient */}
        <linearGradient id={`${uid}-wall`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#334155" />
          <stop offset="50%" stopColor="#1e293b" />
          <stop offset="100%" stopColor="#0f172a" />
        </linearGradient>
        {/* Combustion radial */}
        <radialGradient id={`${uid}-fire`} cx="50%" cy="50%">
          <stop offset="0%" stopColor="#fef3c7" />
          <stop offset="40%" stopColor="#fbbf24" />
          <stop offset="80%" stopColor="#f59e0b" stopOpacity="0.4" />
          <stop offset="100%" stopColor="#f59e0b" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* Chamber */}
      {isVisible("chamber") && (
        <g opacity={partOp("chamber")}>
          {/* 3D shadow beneath */}
          <ellipse cx={cx + depth / 2} cy={chamberTop + chamberH + 14} rx={chamberW * 0.45} ry={8} fill="#000" opacity={0.15} />

          {/* Highlight glow */}
          {isHighlighted("chamber") && (
            <rect x={chamberL - 10} y={chamberTop - 10} width={chamberW + depth + 20} height={chamberH + 20} rx={10} fill={accentColor} opacity={0.06} />
          )}

          {/* Front wall */}
          <rect x={chamberL} y={chamberTop} width={chamberW} height={chamberH} rx={6} fill={`url(#${uid}-wall)`} stroke={partColor("chamber")} strokeWidth={2} />

          {/* Top 3D face (elliptical cap) */}
          <ellipse cx={cx} cy={chamberTop} rx={chamberW / 2} ry={depth} fill="#334155" stroke={partColor("chamber")} strokeWidth={1.5} />
          <ellipse cx={cx} cy={chamberTop - 2} rx={chamberW * 0.35} ry={depth * 0.5} fill="#fff" opacity={0.04} />

          {/* Bottom elliptical cap */}
          <ellipse cx={cx} cy={chamberTop + chamberH} rx={chamberW / 2} ry={depth} fill="#0f172a" stroke={partColor("chamber")} strokeWidth={1.5} />

          {/* Inner side highlights */}
          <line x1={chamberL + 8} y1={chamberTop + depth} x2={chamberL + 8} y2={chamberTop + chamberH - depth} stroke="#fff" strokeWidth={0.8} opacity={0.04} />
          <line x1={chamberR - 8} y1={chamberTop + depth} x2={chamberR - 8} y2={chamberTop + chamberH - depth} stroke="#000" strokeWidth={1} opacity={0.12} />

          {/* Combustion flash */}
          {combustion && (
            <ellipse cx={cx} cy={pistonY - 16} rx={40} ry={24} fill={`url(#${uid}-fire)`} opacity={0.7} />
          )}

          {labels.chamber && (
            <text x={cx} y={chamberTop + chamberH + 34} fill="#94a3b8" textAnchor="middle" fontSize={12} fontWeight={500} fontFamily="'Inter', system-ui, sans-serif">
              {labels.chamber}
            </text>
          )}
        </g>
      )}

      {/* Piston */}
      {isVisible("piston") && (
        <g opacity={partOp("piston")}>
          {isHighlighted("piston") && (
            <rect x={chamberL + 6} y={pistonY - 5} width={chamberW - 12} height={pistonH + 10} rx={8} fill={accentColor} opacity={0.1} />
          )}

          {/* Piston body — metallic */}
          <rect x={chamberL + 12} y={pistonY} width={chamberW - 24} height={pistonH} rx={5} fill={`url(#${uid}-piston)`} stroke={partColor("piston")} strokeWidth={1.5} />

          {/* Piston rings (grooves) */}
          <line x1={chamberL + 16} y1={pistonY + 7} x2={chamberR - 16} y2={pistonY + 7} stroke="#94a3b8" strokeWidth={1.5} opacity={0.6} />
          <line x1={chamberL + 16} y1={pistonY + 14} x2={chamberR - 16} y2={pistonY + 14} stroke="#94a3b8" strokeWidth={1} opacity={0.4} />

          {/* Specular highlight on piston top */}
          <line x1={chamberL + 20} y1={pistonY + 2} x2={chamberR - 20} y2={pistonY + 2} stroke="#fff" strokeWidth={1} opacity={0.15} />

          {/* Connecting rod */}
          <line x1={cx} y1={pistonY + pistonH} x2={cx} y2={chamberTop + chamberH + 35} stroke={partColor("piston")} strokeWidth={5} strokeLinecap="round" />
          <line x1={cx - 1} y1={pistonY + pistonH} x2={cx - 1} y2={chamberTop + chamberH + 35} stroke="#fff" strokeWidth={1} opacity={0.08} />

          {/* Crankshaft — 3D circle */}
          <circle cx={cx} cy={chamberTop + chamberH + 46} r={14} fill="#1e293b" stroke={partColor("piston")} strokeWidth={2} />
          <circle cx={cx - 2} cy={chamberTop + chamberH + 44} r={4} fill="#475569" />
          <circle cx={cx - 3} cy={chamberTop + chamberH + 43} r={1.5} fill="#fff" opacity={0.2} />
        </g>
      )}

      {/* Intake valve (left) */}
      {isVisible("intake_valve") && (
        <g opacity={partOp("intake_valve")}>
          <g transform={`translate(${chamberL - 14}, ${chamberTop + 24})`}>
            {isHighlighted("intake_valve") && (
              <rect x={-6} y={-6} width={36} height={56} rx={8} fill={accentColor} opacity={0.1} />
            )}
            {/* Valve housing — 3D block */}
            <rect x={0} y={0} width={24} height={44} rx={5} fill="#1e293b" stroke={partColor("intake_valve")} strokeWidth={1.5} />
            <rect x={0} y={0} width={24} height={6} rx={3} fill="#334155" stroke={partColor("intake_valve")} strokeWidth={1} />
            {/* Valve stem */}
            <rect
              x={8}
              y={openValves.includes("intake_valve") ? -10 : 6}
              width={8}
              height={22}
              rx={3}
              fill={`url(#${uid}-piston)`}
              stroke={partColor("intake_valve")}
              strokeWidth={1}
            />
            {/* Flow arrow */}
            <path d="M-18,22 L-2,22" stroke={accentColor} strokeWidth={2} strokeLinecap="round" opacity={0.6} />
            <polygon points="-4,18 2,22 -4,26" fill={accentColor} opacity={0.6} />
          </g>
          {labels.intake && (
            <text x={chamberL - 24} y={chamberTop + 88} fill="#94a3b8" textAnchor="middle" fontSize={11} fontWeight={500} fontFamily="'Inter', system-ui, sans-serif">
              {labels.intake}
            </text>
          )}
        </g>
      )}

      {/* Exhaust valve (right) */}
      {isVisible("exhaust_valve") && (
        <g opacity={partOp("exhaust_valve")}>
          <g transform={`translate(${chamberR - 10}, ${chamberTop + 24})`}>
            {isHighlighted("exhaust_valve") && (
              <rect x={-6} y={-6} width={36} height={56} rx={8} fill={accentColor} opacity={0.1} />
            )}
            <rect x={0} y={0} width={24} height={44} rx={5} fill="#1e293b" stroke={partColor("exhaust_valve")} strokeWidth={1.5} />
            <rect x={0} y={0} width={24} height={6} rx={3} fill="#334155" stroke={partColor("exhaust_valve")} strokeWidth={1} />
            <rect
              x={8}
              y={openValves.includes("exhaust_valve") ? -10 : 6}
              width={8}
              height={22}
              rx={3}
              fill={`url(#${uid}-piston)`}
              stroke={partColor("exhaust_valve")}
              strokeWidth={1}
            />
            <path d="M26,22 L42,22" stroke="#ef4444" strokeWidth={2} strokeLinecap="round" opacity={0.6} />
            <polygon points="40,18 46,22 40,26" fill="#ef4444" opacity={0.6} />
          </g>
          {labels.exhaust && (
            <text x={chamberR + 26} y={chamberTop + 88} fill="#94a3b8" textAnchor="middle" fontSize={11} fontWeight={500} fontFamily="'Inter', system-ui, sans-serif">
              {labels.exhaust}
            </text>
          )}
        </g>
      )}
    </g>
  );
};
